import React, { useState, useEffect, useRef } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  placeholderList?: string[];
  debounceMs?: number;
  className?: string;
  fullWidth?: boolean;
}

function SearchBar({
  value,
  onChange,
  onSearch,
  placeholder,
  placeholderList = [],
  debounceMs = 400,
  className = '',
  fullWidth = true,
}: SearchBarProps) {
  // Internal state - completely isolated from parent
  const [internalValue, setInternalValue] = useState(value);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  
  // Refs for everything
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const placeholderTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false);

  // Handle input change - completely isolated
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Update internal state immediately
    setInternalValue(newValue);
    isTypingRef.current = newValue.length > 0;

    // Call onChange immediately
    onChange(newValue);

    // Debounce the search callback
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (onSearch) {
      debounceTimerRef.current = setTimeout(() => {
        onSearch(newValue);
      }, debounceMs);
    }
  };

  // Placeholder cycling - only when not typing
  useEffect(() => {
    if (placeholderList.length <= 1 || isTypingRef.current) {
      return;
    }

    placeholderTimerRef.current = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholderList.length);
    }, 3000);

    return () => {
      if (placeholderTimerRef.current) {
        clearInterval(placeholderTimerRef.current);
      }
    };
  }, [placeholderList.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (placeholderTimerRef.current) {
        clearInterval(placeholderTimerRef.current);
      }
    };
  }, []);

  // Clear handler
  const handleClear = () => {
    setInternalValue('');
    isTypingRef.current = false;
    onChange('');
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    if (onSearch) {
      onSearch('');
    }
    
    inputRef.current?.focus();
  };

  const displayPlaceholder = placeholder || 
    (placeholderList.length > 0 ? placeholderList[currentPlaceholderIndex] : 'Search...');

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={`relative ${widthClass} ${className}`}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg 
          className="h-5 w-5 text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
          />
        </svg>
      </div>

      <input
        ref={inputRef}
        type="text"
        value={internalValue}
        onChange={handleChange}
        placeholder={displayPlaceholder}
        className={`
          ${widthClass}
          pl-10
          ${internalValue ? 'pr-10' : 'pr-3'}
          py-2.5
          bg-white
          border-2 border-gray-300
          rounded-xl
          text-sm
          placeholder-gray-400
          focus:outline-none
          focus:ring-2
          focus:ring-primary-500
          focus:border-primary-500
          transition-all duration-200
          hover:border-gray-400
        `}
      />

      {internalValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          <svg 
            className="h-5 w-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>
      )}
    </div>
  );
}

// NO MEMO - let's see if that's the issue
export default SearchBar;
